import { useState, type FormEvent, type ReactNode } from "react";
import type { LabPanel, LabResult, Me, PetRecord, Prescription, Tone } from "../../../shared/types";
import { api, type SealResult } from "../../api";
import { STATUS_PILL, TONE, conditionName, fmtDate, fmtMonth, fmtRange } from "../../format";
import { currentMedications } from "../../meds";
import { WeightChart } from "./WeightChart";
import { PetQr } from "../PetQr";
import type { TabId } from "./RecordView";

const Tag = ({ label, tone }: { label: string; tone: Tone }) => (
  <span className="htag" style={{ background: TONE[tone]?.bg, color: TONE[tone]?.fg }}>{label}</span>
);
const Pill = ({ tone, children }: { tone: Tone; children: React.ReactNode }) => (
  <span className="pill" style={{ background: TONE[tone].bg, color: TONE[tone].fg }}>{children}</span>
);
const Empty = ({ children }: { children: React.ReactNode }) => <div className="row t2">{children}</div>;

/** Card whose body is hidden until the header is clicked (collapsed by default). */
function Collapsible({ title, summary, children }: { title: string; summary: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card mb">
      <button className="ch as-btn" onClick={() => setOpen(!open)} aria-expanded={open}>
        <div className="ct">{open ? "▾" : "▸"} {title}</div>
        <div className="t3">{summary}</div>
      </button>
      {open && children}
    </div>
  );
}

const days = (n: number) => `${Math.abs(n)}d`;

// ════════ OVERVIEW ════════
export function OverviewTab({ r, goTo }: { r: PetRecord; goTo: (t: TabId) => void }) {
  const { allergies, vaccines } = r;
  const prevention = r.prescriptions.active.filter((p) => p.category === "prevention");
  const screens = vaccines.overdue.concat(vaccines.upcoming).filter((v) => v.detail === "Screening test");
  const clinicBehaviour = r.behaviour.filter((b) => b.source === "clinic" && b.kind !== "note").slice(0, 3);
  const isVaccine = (v: { detail: string }) => v.detail !== "Screening test";
  const overdue = vaccines.overdue.filter(isVaccine);
  const upcoming = vaccines.upcoming.filter(isVaccine);
  const meds = currentMedications(r);

  return (
    <>
      <div className="g3 top3">
        <div className="alg card">
          <div className="alg-sec drug">
            <div className="alg-l">Drug allergies — do not give</div>
            <div className="atags">{allergies.drug.length ? allergies.drug.map((a) => <span key={a} className="atag-red">{a}</span>) : <span className="t2">None recorded</span>}</div>
          </div>
          <div className="alg-sec food">
            <div className="alg-l">Food sensitivities</div>
            <div className="atags">{allergies.food.length ? allergies.food.map((a) => <span key={a} className="atag-amb">{a}</span>) : <span className="t2">None recorded</span>}</div>
          </div>
          <div className="alg-sec medical">
            <div className="alg-l">Medical sensitivities</div>
            <div className="atags">{allergies.medical.length ? allergies.medical.map((a) => <span key={a} className="atag-med">{a}</span>) : <span className="t2">None recorded</span>}</div>
          </div>
        </div>

        <div className="card meds">
          <div className="ch"><div className="ct">Current medications</div><div className="cb bt">{meds.length} active</div></div>
          {meds.length === 0 && <Empty>No current medication on record.</Empty>}
          {meds.map((m) => (
            <div key={m.key} className="med-row" title={m.caution ?? undefined}>
              <div className="med-top">
                <span className="med-n">{m.caution && <span className="med-warn">⚠</span>}{m.name}</span>
                {m.source && <span className="src-pill">{m.source}</span>}
                <span className="med-date">{m.month ? fmtMonth(m.date) : fmtDate(m.date)}</span>
              </div>
              {m.detail && <div className="med-d">{m.detail}</div>}
              {m.caution && <div className="med-c">{m.caution}</div>}
            </div>
          ))}
          <div className="card-foot">
            <span>{r.medsReconciledOn ? `Reconciled ${fmtDate(r.medsReconciledOn)}` : "Not reconciled yet"}</span>
            <button className="link" onClick={() => goTo("rx")}>Prescriptions →</button>
          </div>
        </div>

        <div className="card">
          <div className="ch"><div className="ct">Chronic conditions</div><div className="cb bw">{r.conditions.length ? "Active" : "None"}</div></div>
          {r.conditions.length === 0 && <Empty>No active conditions on record.</Empty>}
          {r.conditions.map((c) => (
            <div key={c.name} className="cond" title={c.detail}>
              <div className="t1">{conditionName(c.name)}</div>
              <div className="cond-s" style={{ color: TONE[STATUS_PILL[c.status]?.tone ?? "warn"].fg }}>
                {STATUS_PILL[c.status]?.label ?? c.status}{c.detail ? <span> · {c.detail}</span> : null}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Collapsible title="Weight trend" summary={r.weights.length ? `${r.weights.at(-1)!.lb} lb on ${fmtDate(r.weights.at(-1)!.date)} · ${r.weights.length} measurement${r.weights.length === 1 ? "" : "s"}` : "No weights recorded"}>
        <div style={{ padding: "8px 14px 5px" }}>
          {r.weights.length ? <WeightChart weights={r.weights} /> : <div className="t2" style={{ padding: 8 }}>No weights recorded.</div>}
        </div>
      </Collapsible>

      <div className="g3">
        <div className="card">
          <div className="ch"><div className="ct">Vaccinations</div>{overdue.length ? <div className="cb bh">⚠ Attention needed</div> : <div className="cb bo">Up to date</div>}</div>
          {overdue.length > 0 && (
            <button className="attn" onClick={() => goTo("vaccines")}>
              <div className="attn-icon" style={{ background: "var(--hl)", color: "var(--hi)" }}>!</div>
              <div><div className="attn-title">{overdue.length} item{overdue.length > 1 ? "s" : ""} overdue</div>
                <div className="attn-sub">{overdue.map((v) => `${v.name} (${days(v.days!)})`).join(" · ")}</div></div>
              <div className="attn-arrow">›</div>
            </button>
          )}
          {upcoming.length > 0 && (
            <button className="attn" onClick={() => goTo("vaccines")}>
              <div className="attn-icon" style={{ background: "var(--wl)", color: "var(--w)" }}>↑</div>
              <div><div className="attn-title">{upcoming.length} booster{upcoming.length > 1 ? "s" : ""} in {upcoming[0].days} days</div>
                <div className="attn-sub">{upcoming.map((v) => v.name).join(" · ")} — {fmtDate(upcoming[0].due)}</div></div>
              <div className="attn-arrow">›</div>
            </button>
          )}
          {!overdue.length && !upcoming.length && <Empty>Nothing due.</Empty>}
        </div>

        <div className="card">
          <div className="ch"><div className="ct">Prevention</div><div className="cb bt">{prevention.length ? "Active" : "—"}</div></div>
          {prevention.map((p) => (
            <div key={p.id} className="row" style={{ alignItems: "center" }}>
              <div style={{ fontSize: 17.5 }}>{p.icon ?? "🐛"}</div>
              <div style={{ flex: 1 }}><div className="t1">{p.name.replace(/\s*\(.*\)$/, "")}</div><div className="t3" style={{ color: "var(--txt2)" }}>{p.instructions}</div></div>
              <Pill tone="ok">Active</Pill>
            </div>
          ))}
          {screens.map((s) => (
            <div key={s.name} className="row" style={{ alignItems: "center" }}>
              <div style={{ fontSize: 17.5 }}>{/fecal/i.test(s.name) ? "🪱" : "🦟"}</div>
              <div style={{ flex: 1 }}><div className="t1">{s.name}</div><div className="t3" style={{ color: "var(--txt2)" }}>Due {fmtDate(s.due)}</div></div>
              <Pill tone={s.days! < 0 ? "high" : "warn"}>{s.days! < 0 ? "Overdue" : `Due ${s.due!.slice(0, 4)}`}</Pill>
            </div>
          ))}
          {!prevention.length && !screens.length && <Empty>No prevention on record.</Empty>}
        </div>

        <div className="card">
          <div className="ch"><div className="ct">Behavior</div><div className="cb bg">Clinic notes</div></div>
          {clinicBehaviour.map((b) => (
            <div key={b.id} className="row">
              <div style={{ fontSize: 15.5, marginTop: 1 }}>{b.kind === "watch" ? "⚠" : "✓"}</div>
              <div style={{ fontSize: 12, lineHeight: 1.5 }}>{b.headline && <span style={{ fontWeight: 500 }}>{b.headline}</span>}{b.headline ? " — " : ""}{b.body}</div>
            </div>
          ))}
          {!clinicBehaviour.length && <Empty>No clinic observations yet.</Empty>}
        </div>
      </div>
    </>
  );
}

// ════════ VISITS ════════
const DOT: Record<string, string> = { high: "#D94040", warn: "#E8A020", ok: "#3A8C6E", teal: "#2BBCAA", purple: "#2BBCAA", info: "#4A6BBA" };

function VisitExtras({ v }: { v: PetRecord["visits"][number] }) {
  const s = v.vitals;
  if (!s && !v.treatments.length) return null;
  const vit = s ? [s.tempF && `${s.tempF} °F`, s.heartRate && `HR ${s.heartRate}`, s.respRate && `RR ${s.respRate}`, s.painScore !== null && `pain ${s.painScore}/10`].filter(Boolean) : [];
  return (
    <div className="visit-extras">
      {s && (
        <div>
          <b>Triage</b> <span className="t3">· {s.recordedBy}</span>
          {vit.length > 0 && <div className="vit-pills">{vit.map((x) => <span key={String(x)}>{x}</span>)}</div>}
          {s.anamnesis && <div><i>History:</i> {s.anamnesis}</div>}
        </div>
      )}
      {v.treatments.length > 0 && (
        <div><b>Given in clinic</b> <span className="t3">· {v.treatments[0].givenBy}</span>
          <ul>{v.treatments.map((t, i) => <li key={i}>{t.description}{t.dose ? ` — ${t.dose}` : ""} <span className="t3">({t.kind})</span></li>)}</ul>
        </div>
      )}
    </div>
  );
}

export function VisitsTab({ r, open, setOpen }: { r: PetRecord; open: number | null; setOpen: (id: number | null) => void }) {
  if (!r.visits.length) return <div className="card"><Empty>No visits recorded yet.</Empty></div>;
  const [latest, ...older] = r.visits;
  const dateOf = (v: PetRecord["visits"][number]) => v.dateLabel ?? fmtRange(v.date, v.endDate);
  return (
    <div className="card">
      <div id={`visit-${latest.id}`} style={{ padding: "12px 14px", borderBottom: "0.5px solid var(--bd)" }} className={open === latest.id ? "visit-flash" : ""}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <div className="hdot" style={{ background: "#2BBCAA" }} />
          <div className="t3">{dateOf(latest)}{latest.visitNo ? ` · ${latest.visitNo}` : ""} <span className="pill" style={{ marginLeft: 6, background: "var(--tl)", color: "var(--teal)" }}>Latest</span></div>
        </div>
        <div className="t1" style={{ marginBottom: 6 }}>{latest.title}</div>
        <div style={{ fontSize: 13, lineHeight: 1.75 }}>{latest.notes || latest.summary}</div>
        <VisitExtras v={latest} />
        <div style={{ marginTop: 8 }}>{latest.tags.map((t) => <Tag key={t.label} {...t} />)}</div>
        <div className="visit-body" style={{ padding: 0, border: "none", background: "none" }}><div className="by">{latest.vet} · {latest.clinic}{latest.blockSeq !== null ? ` · sealed in block #${latest.blockSeq}` : ""}</div></div>
      </div>
      {older.map((v) => {
        const isOpen = open === v.id;
        return (
          <div key={v.id} id={`visit-${v.id}`}>
            <button className="visit-head" onClick={() => setOpen(isOpen ? null : v.id)} aria-expanded={isOpen}>
              <div className="hdot" style={{ background: DOT[v.tags[0]?.tone ?? "ok"] }} />
              <div style={{ flex: 1 }}>
                <div className="t3">{dateOf(v)}{v.visitNo ? ` · ${v.visitNo}` : ""}</div>
                <div style={{ fontSize: 13, marginTop: 1 }}>{v.summary}</div>
              </div>
              <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                {v.tags.map((t) => <Tag key={t.label} {...t} />)}
                <span style={{ fontSize: 15.5, color: "var(--txt3)", marginLeft: 4 }}>{isOpen ? "↓" : "›"}</span>
              </div>
            </button>
            {isOpen && (
              <div className={`visit-body${open === v.id ? " visit-flash" : ""}`}>
                {v.notes || v.summary}
                <VisitExtras v={v} />
                <div className="by">{v.vet} · {v.clinic}{v.blockSeq !== null ? ` · sealed in block #${v.blockSeq}` : ""}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ════════ LABS ════════
function rangePos(x: LabResult): { left: number; width: number; color: string } {
  const color = x.flag === "H" ? "#D94040" : x.flag === "L" ? "#4A6BBA" : "#2BBCAA";
  if (x.valueNum === null || x.refLow === null || x.refHigh === null || x.refHigh === x.refLow) return { left: 0, width: 0, color };
  const pct = ((x.valueNum - x.refLow) / (x.refHigh - x.refLow)) * 100;
  return { left: 0, width: Math.max(4, Math.min(100, pct)), color };
}
const fmtNum = (n: number) => (Number.isInteger(n) ? String(n) : String(n));
const fmtRef = (x: LabResult) => x.refText ?? (x.refLow !== null && x.refHigh !== null ? `${fmtNum(x.refLow)}–${fmtNum(x.refHigh)}${x.unit ? ` ${x.unit}` : ""}` : "—");

function NumericPanel({ p }: { p: LabPanel }) {
  const flags = p.results.filter((x) => x.flag === "H" || x.flag === "L").length;
  return (
    <div className="card mb">
      <div className="ch"><div className="ct">{p.name}</div><div className={`cb ${flags ? "bw" : "bo"}`}>{flags ? `${flags} flag${flags > 1 ? "s" : ""}` : "Normal"}</div></div>
      <div className="lr head"><div>Test</div><div>Result</div><div>Reference</div><div>Range</div><div>Flag</div></div>
      {p.results.map((x) => {
        const bar = rangePos(x);
        const cls = x.flag === "H" || x.flag === "POS" ? "chi" : x.flag === "L" ? "clo" : "cok";
        return (
          <div key={x.analyte} className="lr">
            <div>{x.analyte}</div>
            <div className={`lv ${cls}`}>{x.valueNum !== null ? fmtNum(x.valueNum) : x.valueText}{x.unit && x.valueNum !== null ? ` ${x.unit}` : ""}</div>
            <div className="lrf">{fmtRef(x)}</div>
            <div>{bar.width > 0 && <div className="bar"><i style={{ left: 0, width: `${bar.width}%`, background: bar.color }} /></div>}</div>
            <div className={`fl ${x.flag === "H" || x.flag === "POS" ? "fh" : x.flag === "L" ? "flo" : "fok"}`}>{x.flag === "N" ? "OK" : x.flag === "H" ? "HI" : x.flag === "L" ? "LO" : x.flag}</div>
          </div>
        );
      })}
    </div>
  );
}

function QualitativePanel({ p }: { p: LabPanel }) {
  const positive = p.results.some((x) => x.flag === "POS" || x.flag === "H");
  return (
    <div className="card">
      <div className="ch"><div className="ct">{p.name} ({fmtDate(p.collectedOn)})</div><div className={`cb ${positive ? "bh" : "bo"}`}>{positive ? "Findings" : "Clear"}</div></div>
      {p.results.map((x) => (
        <div key={x.analyte} className="vr">
          <div className="vn">{x.analyte}</div>
          <div className={`cb ${x.flag === "NEG" || x.flag === "N" ? "bo" : "bh"}`}>{x.valueText ?? (x.valueNum !== null ? `${x.valueNum}${x.unit ? ` ${x.unit}` : ""}` : "—")}</div>
        </div>
      ))}
    </div>
  );
}

const MODALITY_ICON: Record<string, string> = { xray: "🩻", microscopy: "🔬", scatter: "📊", ultrasound: "🩺", other: "🖼" };

export function LabsTab({ r }: { r: PetRecord }) {
  const numeric = r.labs.filter((p) => p.results.some((x) => x.valueNum !== null && x.refLow !== null));
  const qualitative = r.labs.filter((p) => !numeric.includes(p));
  return (
    <>
      {numeric.map((p) => (
        <div key={p.id}>
          <div className="sec">{p.name} — {fmtDate(p.collectedOn)}{p.labRef ? ` · ${p.labRef}` : ""}</div>
          <NumericPanel p={p} />
        </div>
      ))}
      {qualitative.length > 0 && (
        <>
          <div className="sec">Qualitative tests</div>
          <div className="g2">{qualitative.map((p) => <QualitativePanel key={p.id} p={p} />)}</div>
        </>
      )}
      {r.imaging.length > 0 && <div className="sec" style={{ marginTop: 16 }}>Diagnostic Imaging &amp; Microscopy</div>}
      {r.imaging.map((i) => (
        <div key={i.id} className="card mb">
          <div className="ch"><div className="ct">{i.title}</div><div className={`cb ${i.modality === "xray" ? "bw" : "bp"}`}>{fmtDate(i.studyDate)}</div></div>
          <div style={{ padding: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 12 }}>
              {i.views.map((v) => (
                <div key={v.label} className={`tile${i.modality === "xray" ? "" : " light"}`}>
                  <div className="e">{MODALITY_ICON[i.modality]}</div>
                  <div className="l">{v.label}{v.sub && <><br />{v.sub}</>}</div>
                </div>
              ))}
            </div>
            <div className="report">{i.report}</div>
          </div>
        </div>
      ))}
      {!r.labs.length && !r.imaging.length && <div className="card"><Empty>No lab work on record.</Empty></div>}
    </>
  );
}

// ════════ BEHAVIOUR ════════
export function BehaviourTab({ r, me, onSaved }: { r: PetRecord; me: Me; onSaved: (s: SealResult) => void }) {
  const positive = r.behaviour.filter((b) => b.kind === "positive");
  const watch = r.behaviour.filter((b) => b.kind === "watch");
  const notes = r.behaviour.filter((b) => b.kind === "note");
  const [kind, setKind] = useState<"note" | "positive" | "watch">("note");
  const [headline, setHeadline] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const s = await api.addNote(r.pet.id, { kind, headline: headline || undefined, body });
      setHeadline(""); setBody("");
      onSaved(s);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const line = (b: PetRecord["behaviour"][number]) => (
    <li key={b.id} title={`${b.source === "owner" ? "Owner reported" : "Clinic"} · ${b.author} · ${fmtDate(b.notedOn)}`}>
      {b.kind === "watch" ? "⚠ " : "✓ "}{b.headline ?? b.body}{b.headline ? <span style={{ color: "var(--txt2)" }}> — {b.body}</span> : null}
      <span className="t3"> · {b.source === "owner" ? "owner" : "clinic"}</span>
    </li>
  );

  return (
    <>
      <div className="card mb">
        <div className="ch"><div className="ct">Behavioral profile</div><div className="cb bt">Clinic &amp; owner reported</div></div>
        <div style={{ padding: 14 }}>
          <div className="g2" style={{ marginBottom: 12 }}>
            <div className="box" style={{ background: "#EAF5F0" }}><div className="box-h" style={{ color: "var(--ok)" }}>Positive traits</div><ul>{positive.length ? positive.map(line) : <li>—</li>}</ul></div>
            <div className="box" style={{ background: "#FEF5E6" }}><div className="box-h" style={{ color: "var(--w)" }}>Watch items</div><ul>{watch.length ? watch.map(line) : <li>— No handling concerns on record</li>}</ul></div>
          </div>
          {notes.map((n) => (
            <div key={n.id} className="box" style={{ background: "#F4F8FA", marginBottom: 8 }}>
              <div className="box-h" style={{ color: "var(--nt)" }}>{n.source === "clinic" ? "Clinic notes" : "Owner notes"} · <span className="t3" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>{n.author}, {fmtDate(n.notedOn)}</span></div>
              <div style={{ fontSize: 13, color: "var(--txt2)", lineHeight: 1.7 }}>{n.headline ? <b>{n.headline}. </b> : null}{n.body}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="card mb">
        <div className="ch"><div className="ct">Activity &amp; lifestyle</div><div className="cb bt">Owner reported</div></div>
        {Object.entries(r.pet.lifestyle).map(([k, v]) => (
          <div key={k} className="vr"><div className="vn">{k}</div><div style={{ fontSize: 13, color: "var(--txt2)", textAlign: "right" }}>{v}</div></div>
        ))}
        {!Object.keys(r.pet.lifestyle).length && <Empty>Not recorded.</Empty>}
      </div>
      <form className="card" onSubmit={save}>
        <div className="ch"><div className="ct">Add behavioral note</div><div className="cb bg">{me.role === "vet" ? "Clinic use" : "Owner report"}</div></div>
        <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="frow">
            <div className="field"><label>Type</label>
              <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
                <option value="note">General note</option><option value="positive">Positive trait</option><option value="watch">Watch item / caution</option>
              </select>
            </div>
            <div className="field"><label>Headline (optional)</label><input value={headline} maxLength={80} onChange={(e) => setHeadline(e.target.value)} placeholder="e.g. Anxious on entry" /></div>
          </div>
          <div className="field">
            <textarea value={body} maxLength={2000} onChange={(e) => setBody(e.target.value)} required
              placeholder="Enter observation from this visit (e.g. anxious on entry, calm during blood draw…)" />
          </div>
          {error && <div className="err">{error}</div>}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="fine">Notes are sealed into the record chain and cannot be edited afterwards.</span>
            <button className="btn teal" disabled={busy || !body.trim()}>{busy ? "Sealing…" : "Save note"}</button>
          </div>
        </div>
      </form>
    </>
  );
}

// ════════ PRESCRIPTIONS ════════
const RX_BG: Record<Prescription["category"], string> = { medication: "var(--pl)", prevention: "#E8F4EE", diet: "var(--tl)", topical: "var(--ll)" };

export function RxTab({ r }: { r: PetRecord }) {
  const { active, diet, past } = r.prescriptions;
  return (
    <>
      <div className="card mb">
        <div className="ch"><div className="ct">Active prescriptions</div><div className="cb bp">{active.length} active</div></div>
        {!active.length && <Empty>No active prescriptions.</Empty>}
        {active.map((p) => (
          <div key={p.id} className="rx">
            <div className="rx-ico" style={{ background: RX_BG[p.category] }}>{p.icon ?? "💊"}</div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3, gap: 8 }}>
                <div className="rx-n">{p.name}</div>
                {p.status === "ongoing" ? <Pill tone="ok">Ongoing</Pill> : <Pill tone="purple">Active until {fmtDate(p.endsOn)}</Pill>}
              </div>
              <div className="t2" style={{ lineHeight: 1.6 }}>{p.instructions}</div>
              <div className="rx-meta">
                {p.rxNo && <div><b>Rx ID:</b> {p.rxNo}</div>}
                <div><b>{p.status === "ongoing" ? "Started" : "Prescribed"}:</b> {fmtDate(p.startedOn)}</div>
                {p.refills !== null && <div><b>Refills:</b> {p.refills}</div>}
                <div><b>Provider:</b> {p.vet}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="card mb">
        <div className="ch"><div className="ct">Prescription diet</div><div className="cb bt">{diet.length ? "Active" : "—"}</div></div>
        {!diet.length && <Empty>No prescription diet.</Empty>}
        {diet.map((p) => (
          <div key={p.id} className="rx">
            <div className="rx-ico" style={{ background: "var(--tl)" }}>{p.icon ?? "🥣"}</div>
            <div style={{ flex: 1 }}>
              <div className="rx-n" style={{ marginBottom: 3 }}>{p.name}</div>
              <div className="t2" style={{ lineHeight: 1.6 }}>{p.instructions}{p.indication ? ` · ${p.indication}` : ""}</div>
              <div className="rx-meta"><div><b>First dispensed:</b> {fmtDate(p.firstDispensed)}</div><div><b>Last supplied:</b> {fmtDate(p.lastSupplied)}</div></div>
            </div>
          </div>
        ))}
      </div>
      <div className="card">
        <div className="ch"><div className="ct">Past prescriptions</div><div className="cb bg">Completed</div></div>
        {!past.length && <Empty>None.</Empty>}
        {past.map((p) => (
          <div key={p.id} className="rx past">
            <div className="rx-ico">{p.icon ?? "💊"}</div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div className="rx-n">{p.name}</div>
                <div className="t3">{fmtRange(p.startedOn, p.endsOn)}</div>
              </div>
              <div className="t3" style={{ fontSize: 12 }}>{[p.instructions, p.indication, p.rxNo && `Rx ${p.rxNo}`, p.vet].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ════════ VACCINES ════════
export function VaccinesTab({ r }: { r: PetRecord }) {
  const { overdue, upcoming, given } = r.vaccines;
  return (
    <>
      <div className="card mb">
        <div className="ch"><div className="ct">Overdue</div>{overdue.length ? <div className="cb bh">Action needed</div> : <div className="cb bo">None</div>}</div>
        {!overdue.length && <Empty>Nothing overdue.</Empty>}
        {overdue.map((v) => (
          <div key={v.name} className="vr"><div><div className="vn">{v.name}</div><div className="vd">Due {fmtDate(v.due)}</div></div><div className="vo vop">{days(v.days!)} overdue</div></div>
        ))}
      </div>
      <div className="card mb">
        <div className="ch"><div className="ct">Upcoming</div><div className="cb bw">Schedule</div></div>
        {!upcoming.length && <Empty>Nothing scheduled.</Empty>}
        {upcoming.map((v) => (
          <div key={v.name} className="vr"><div><div className="vn">{v.name}</div><div className="vd">{fmtDate(v.due)}</div></div><div className="vo vod">in {days(v.days!)}</div></div>
        ))}
      </div>
      <div className="card">
        <div className="ch"><div className="ct">Given on record</div><div className="cb bo">On file</div></div>
        {!given.length && <Empty>No vaccinations on record.</Empty>}
        {given.map((v, i) => (
          <div key={i} className="vr"><div><div className="vn">{v.name}</div><div className="vd">{fmtDate(v.given)}{v.detail ? ` · ${v.detail}` : ""}</div></div><div className="vo vog">Given</div></div>
        ))}
      </div>
    </>
  );
}

// ════════ OWNER & ID ════════
export function OwnerTab({ r, kg }: { r: PetRecord; kg: number | null }) {
  const { pet, owner, lastClinic } = r;
  const rows: [string, React.ReactNode][] = [
    ["Name", <span style={{ fontWeight: 500 }}>{pet.displayName}</span>],
    ["Species", pet.species], ["Breed", pet.breed ?? "—"], ["Sex", pet.sex ?? "—"], ["Color", pet.color ?? "—"],
    ["Age", pet.ageLabel ?? "—"], ["Weight", pet.weightLb ? `${pet.weightLb} lb (${kg} kg)` : "—"],
    ["Microchip", <span data-notranslate style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{pet.microchip}</span>],
  ];
  return (
    <>
      <div className="g2">
        <div className="card">
          <div className="ch"><div className="ct">Patient identity</div><div className="cb bt">ID: {pet.patientNo}</div></div>
          <div style={{ padding: "10px 14px" }}>{rows.map(([k, v]) => <div key={k} className="or2"><div className="ok3">{k}</div><div>{v}</div></div>)}</div>
        </div>
        <div className="card">
          <div className="ch"><div className="ct">Owner contact</div><div className="cb bt">Primary</div></div>
          <div style={{ padding: "10px 14px" }}>
            {owner ? (
              <>
                <div className="or2"><div className="ok3">Name</div><div style={{ fontWeight: 500 }}>{owner.name}</div></div>
                <div className="or2"><div className="ok3">Address</div><div style={{ whiteSpace: "pre-line" }}>{owner.address ?? "—"}</div></div>
                <div className="or2"><div className="ok3">Mobile</div><div data-notranslate>{owner.phone ?? "—"}</div></div>
                <div className="or2"><div className="ok3">Email</div><div data-notranslate style={{ fontSize: 12 }}>{owner.email ?? "—"}</div></div>
              </>
            ) : <div className="t2">No owner on record.</div>}
          </div>
        </div>
      </div>
      <div className="card" style={{ marginTop: 10 }}>
        <div className="ch"><div className="ct">Vetproof QR</div><div className="cb bt">Owner ID card</div></div>
        <PetQr chip={pet.microchip} name={pet.name} />
      </div>
      <div className="card" style={{ marginTop: 10 }}>
        <div className="ch"><div className="ct">Last treating clinic</div></div>
        <div style={{ padding: "10px 14px", fontSize: 13, color: "#4A6B7C" }}>
          {lastClinic ? [lastClinic.name, lastClinic.address, lastClinic.phone, lastClinic.vet].filter(Boolean).join(" · ") : "—"}
        </div>
      </div>
    </>
  );
}
